import React, { useCallback, useContext, useState } from 'react'
import { AutoColumn } from 'components/Layout/Column'
import { RowFixed } from 'components/Layout/Row'
import CurrencyLogo from 'components/Logo/CurrencyLogo'
import QuestionHelper from 'components/QuestionHelper'
import Balance from 'components/Balance'
import LocalBalance from 'components/LocalBalance'
import tokens from 'config/constants/tokens'
import { useTranslation } from 'contexts/Localization'
import styled from 'styled-components'
import { FixedHeightRow } from 'views/xVvs/styles'
import {
  Card,
  ChevronUpIcon,
  ChevronDownIcon,
  Flex,
  Text,
  Button,
  AddIcon,
  Image,
  TokenAmount,
  useModal,
  useMatchBreakpoints,
  Box,
  TooltipText,
  useTooltip,
} from 'vvs-uikit'
import { formatLockPeriod } from 'views/xVvs/helpers'
import boostPools from 'config/constants/boostPools'
import { formatNumber, getBalanceAmount } from 'utils/formatBalance'
import BigNumber from 'bignumber.js'
import xVvsContext from 'views/xVvs/context'
import useTheme from 'hooks/useTheme'
import { useVaultsApr } from 'views/xVvs/hooks/useVaultsApr'
import usePriceXvvsUsdc from 'views/xVvs/hooks/usePriceXvvsUsdc'
import { format, fromUnixTime } from 'date-fns'
import usePastWeekApr from 'views/xVvs/hooks/usePastWeekApr'
import LockModal from '../LockModal'
import LockPeriodLogo from '../LockPeriodLogo'
import UpgradeModal from '../UpgradeModal'
import WithdrawalModal from '../WithdrawModal'
import EarningTooltipContent from './EarningTooltipContent'

const StyledCard = styled(Card)`
  &:last-child {
    margin-bottom: 0;
  }
`
const StyledCardMini = styled(Card)`
  max-width: 330px;

  ${({ theme }) => theme.mediaQueries.sm} {
    margin: 0 12px 46px;
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    max-width: 380px;
  }
`

const StyledBox = styled(Box)`
  display: block;

  ${({ theme }) => theme.mediaQueries.sm} {
    display: flex;
    flex: 1;
    justify-content: space-between;
  }
`

interface VaultInnerCardProps {
  poolId: number
  totalDeposited: BigNumber
  totalLocked: BigNumber
  totalUnlocked: BigNumber
  stakeIds: number[]
  withdrawableStakeIds: number[]
  lastUnlockTimestamp: number
}

export const VaultCardMini = ({
  poolId,
  totalDeposited,
  totalLocked,
  totalUnlocked,
  stakeIds,
  withdrawableStakeIds,
  lastUnlockTimestamp,
}: VaultInnerCardProps) => {
  const { t } = useTranslation()

  const { selectLockPeriod, setStep, onReset } = useContext(xVvsContext)

  const [openUpgradeModal] = useModal(
    <UpgradeModal customOnDismiss={onReset} stakeIds={stakeIds} currentPid={poolId} amount={totalDeposited} />,
  )
  const [openLockModal] = useModal(<LockModal customOnDismiss={onReset} />, false, true, 'depositMoreModal')
  const [openWithdrawModal] = useModal(<WithdrawalModal stakeIds={withdrawableStakeIds} />)
  const vaultsApr = useVaultsApr()
  const xvvsPastWeekApr = usePastWeekApr()

  const handleDepositMore = () => {
    selectLockPeriod(poolId)
    setStep(1)
    openLockModal()
  }

  const handleOpenUpgradeModal = () => {
    onReset()
    openUpgradeModal()
  }

  const currentPool = boostPools.find((x) => x.pid === poolId)
  const { lockPeriod, imgSrc, multiplier } = currentPool
  const apr = vaultsApr.find((pool) => pool.poolId === poolId)?.apr || '0'
  const vaultApr = (Number(apr) - xvvsPastWeekApr).toFixed(3)

  const depositedAmount = getBalanceAmount(totalDeposited, tokens.xvvs.decimals)
  const lockedAmount = getBalanceAmount(totalLocked, tokens.xvvs.decimals)
  const unlockedAmount = getBalanceAmount(totalUnlocked, tokens.xvvs.decimals)
  const lastUnlockDate = format(fromUnixTime(lastUnlockTimestamp), 'MMM dd, yyyy')

  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    EarningTooltipContent({ vaultApr, xvvsPastWeekApr: xvvsPastWeekApr.toFixed(3) }),
    {
      placement: 'bottom-start',
    },
  )

  return (
    <StyledCardMini p={0} mb="24px">
      <Flex justifyContent="space-between" p="16px">
        <Flex flex={1} alignItems="center">
          <CurrencyLogo size="42px" currency={tokens.xvvs} />
        </Flex>
        <Flex justifyContent="flex-end" alignItems="center" mb="4px" flex={1}>
          <LockPeriodLogo imgSrc={imgSrc} width={42} height={42} />
          <Text bold ml="8px">
            {formatLockPeriod(lockPeriod)}
          </Text>
        </Flex>
      </Flex>
      <Flex px="16px">
        <Text fontSize="20px">
          {formatNumber(depositedAmount.toNumber())} {tokens.xvvs.symbol}
        </Text>
      </Flex>
      <AutoColumn gap="8px" style={{ padding: '16px' }}>
        <Flex justifyContent="space-between">
          <RowFixed>
            {tooltipVisible && tooltip}
            <TooltipText small color="textSubtle" ref={targetRef}>
              APR
            </TooltipText>
          </RowFixed>
          <RowFixed>
            <Text small bold>
              {formatNumber(parseFloat(apr), 0, 2)}%
            </Text>
          </RowFixed>
        </Flex>
        <Flex justifyContent="space-between">
          <RowFixed>
            <Text small color="textSubtle">
              {t('Multiplier')}
            </Text>
          </RowFixed>
          <RowFixed>
            <Text small bold>
              {multiplier / 100}x
            </Text>
          </RowFixed>
        </Flex>
        <Flex justifyContent="space-between">
          <RowFixed>
            <Text small color="textSubtle">
              {t('Total locked')}
            </Text>
          </RowFixed>
          <RowFixed>
            <Text small bold>
              {formatNumber(lockedAmount.toNumber(), 2, 6)}
            </Text>
          </RowFixed>
        </Flex>
        <Flex justifyContent="space-between" mt={3}>
          <RowFixed>
            <Text small color="textSubtle">
              {t('Last unlock date')}
            </Text>
          </RowFixed>
          <RowFixed>
            <Text small bold>
              {lastUnlockDate}
            </Text>
          </RowFixed>
        </Flex>
        <Flex justifyContent="space-between" alignItems="center" mb="16px">
          <Flex flexDirection="column">
            <Flex alignItems="center">
              <Text small color="textSubtle">
                {t('Total xVVS unlocked')}
              </Text>
              <QuestionHelper
                text={t('You can still keep unlocked xVVS in your vault to continue earning rewards.')}
                ml="4px"
                mt="3px"
                placement="auto-start"
              />
            </Flex>
            <TokenAmount color="textSubtle">{formatNumber(unlockedAmount.toNumber(), 2, 6)}</TokenAmount>
          </Flex>
          <Button disabled={unlockedAmount.isEqualTo('0')} variant="secondary" onClick={openWithdrawModal}>
            {t('Withdraw')}
          </Button>
        </Flex>

        <Flex flexDirection="column">
          {poolId !== 3 && (
            <Button
              variant="primary"
              width="100%"
              mb="8px"
              scale="lg"
              startIcon={<Image alt="rocket icon" src="/images/xVVS/rocket.png" width={24} height={24} />}
              onClick={handleOpenUpgradeModal}
            >
              {t('Upgrade Locking Period')}
            </Button>
          )}
          <Button
            endIcon={
              <QuestionHelper
                text={t(
                  'Additional deposits will count as new lock time line that wouldn’t affect any of existing locked xVVS.',
                )}
                ml="4px"
                mt="4px"
                placement="auto-start"
              />
            }
            variant="text"
            startIcon={<AddIcon color="primary" />}
            width="100%"
            onClick={handleDepositMore}
          >
            {t('Deposit More')}
          </Button>
        </Flex>
      </AutoColumn>
    </StyledCardMini>
  )
}

export default function VaultInnerCard({
  poolId,
  totalDeposited,
  totalLocked,
  totalUnlocked,
  stakeIds,
  withdrawableStakeIds,
  lastUnlockTimestamp,
}: VaultInnerCardProps) {
  const { t } = useTranslation()

  const { selectLockPeriod, setStep, onReset } = useContext(xVvsContext)
  const { theme } = useTheme()
  const { isMobile } = useMatchBreakpoints()

  const vaultsApr = useVaultsApr()
  const xvvsPastWeekApr = usePastWeekApr()

  const [showMore, setShowMore] = useState<boolean>()
  const [openUpgradeModal] = useModal(
    <UpgradeModal customOnDismiss={onReset} stakeIds={stakeIds} currentPid={poolId} amount={totalDeposited} />,
  )
  const [openLockModal] = useModal(<LockModal customOnDismiss={onReset} />, false, true, 'depositMoreModal')
  const [openWithdrawModal] = useModal(<WithdrawalModal stakeIds={withdrawableStakeIds} />)
  const toggleContent = useCallback(() => {
    setShowMore((show) => !show)
  }, [])

  const handleDepositMore = () => {
    selectLockPeriod(poolId)
    setStep(1)
    openLockModal()
  }

  const handleOpenUpgradeModal = () => {
    onReset()
    openUpgradeModal()
  }

  const currentPool = boostPools.find((x) => x.pid === poolId)
  const { lockPeriod, imgSrc, multiplier } = currentPool

  const depositedAmount = getBalanceAmount(totalDeposited, tokens.xvvs.decimals)
  const lockedAmount = getBalanceAmount(totalLocked, tokens.xvvs.decimals)
  const unlockedAmount = getBalanceAmount(totalUnlocked, tokens.xvvs.decimals)
  const lastUnlockDate = format(fromUnixTime(lastUnlockTimestamp), 'MMM dd, yyyy')
  const apr = vaultsApr.find((x) => x.poolId === poolId)?.apr || '0'
  const vaultApr = (Number(apr) - xvvsPastWeekApr).toFixed(3)
  const xvvsPriceUsd = usePriceXvvsUsdc()

  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    EarningTooltipContent({ vaultApr, xvvsPastWeekApr: xvvsPastWeekApr.toFixed(3) }),
    {
      placement: 'bottom-start',
    },
  )

  return (
    <StyledCard p={0} mb="24px" background={theme.colors.invertedContrast}>
      <Flex justifyContent="space-between" role="button" onClick={toggleContent} p="24px">
        <StyledBox>
          <Flex alignItems="center" mb="4px">
            <Box display={isMobile ? 'none' : 'block'} pr="8px">
              <LockPeriodLogo imgSrc={imgSrc} width={42} height={42} />
            </Box>
            <Text bold>{formatLockPeriod(lockPeriod)}</Text>
          </Flex>
          <Flex alignItems="center" mt={isMobile ? 2 : 0}>
            <Box mr="8px">
              <Flex alignItems="center" mt={isMobile ? 2 : 0}>
                <CurrencyLogo size="20px" currency={tokens.xvvs} />
                <Balance value={depositedAmount.toNumber()} color="text" bold ml="8px" />
              </Flex>
              <Flex justifyContent="end">
                <LocalBalance
                  prefix="~ "
                  color="textSubtle"
                  fontSize="9.75px"
                  value={depositedAmount.times(xvvsPriceUsd)}
                />
              </Flex>
            </Box>
          </Flex>
        </StyledBox>
        {showMore ? <ChevronUpIcon color={theme.colors.primary} /> : <ChevronDownIcon color={theme.colors.primary} />}
      </Flex>
      {showMore && (
        <AutoColumn gap="8px" style={{ padding: '24px', borderTop: `2px solid ${theme.colors.backgroundAlt3}` }}>
          <FixedHeightRow style={{ padding: 0 }}>
            <RowFixed>
              {tooltipVisible && tooltip}
              <TooltipText small color="textSubtle" ref={targetRef}>
                APR
              </TooltipText>
            </RowFixed>
            <RowFixed>
              <Text small bold>
                {formatNumber(parseFloat(apr))}%
              </Text>
            </RowFixed>
          </FixedHeightRow>
          <FixedHeightRow style={{ padding: 0 }}>
            <RowFixed>
              <Text small color="textSubtle">
                {t('Multiplier')}
              </Text>
            </RowFixed>
            <RowFixed>
              <Text small bold>
                {multiplier / 100}x
              </Text>
            </RowFixed>
          </FixedHeightRow>
          <FixedHeightRow style={{ padding: 0 }}>
            <RowFixed>
              <Text small color="textSubtle">
                {t('Total locked')}
              </Text>
            </RowFixed>
            <RowFixed>
              <Text small bold>
                {formatNumber(lockedAmount.toNumber(), 2, 6)}
              </Text>
            </RowFixed>
          </FixedHeightRow>
          <FixedHeightRow style={{ padding: 0 }} mt={3}>
            <RowFixed>
              <Text small color="textSubtle">
                {t('Last unlock date')}
              </Text>
            </RowFixed>
            <RowFixed>
              <Text small bold>
                {lastUnlockDate}
              </Text>
            </RowFixed>
          </FixedHeightRow>
          <Flex justifyContent="space-between" alignItems="center" mb="16px">
            <Flex flexDirection="column">
              <Flex alignItems="center">
                <Text small color="textSubtle" style={{ whiteSpace: 'nowrap' }}>
                  {t('Total xVVS unlocked')}
                </Text>
                <QuestionHelper
                  text={t('You can still keep unlocked xVVS in your vault to continue earning rewards.')}
                  ml="4px"
                  mt="3px"
                  placement="auto-start"
                />
              </Flex>
              <TokenAmount color="textSubtle">{formatNumber(unlockedAmount.toNumber(), 2, 6)}</TokenAmount>
            </Flex>
            <Button disabled={unlockedAmount.isEqualTo('0')} variant="secondary" onClick={openWithdrawModal}>
              {t('Withdraw')}
            </Button>
          </Flex>

          <Flex flexDirection="column">
            {poolId !== 3 && (
              <Button
                variant="primary"
                width="100%"
                mb="8px"
                scale="lg"
                startIcon={<Image alt="rocket icon" src="/images/xVVS/rocket.png" width={24} height={24} />}
                onClick={handleOpenUpgradeModal}
              >
                {t('Upgrade Locking Period')}
              </Button>
            )}
            <Button
              endIcon={
                <QuestionHelper
                  text={t(
                    'Additional deposits will count as new lock time line that wouldn’t affect any of existing locked xVVS.',
                  )}
                  ml="4px"
                  mt="4px"
                  placement="auto-start"
                />
              }
              variant="text"
              startIcon={<AddIcon color="primary" />}
              width="100%"
              onClick={handleDepositMore}
            >
              {t('Deposit More')}
            </Button>
          </Flex>
        </AutoColumn>
      )}
    </StyledCard>
  )
}
